import { Component, Input, OnInit, OnChanges } from '@angular/core';
import { ApiService } from 'src/app/services/api.service';
import { IpcV2Service, IpcChannel } from 'src/app/services/ipc-v2.service';

interface ParsedElement {
  type: 'text' | 'image' | 'link' | 'video' | 'audio' | 'pdf';
  content: string;
  url?: string;
  title?: string;
}

@Component({
  selector: 'app-rich-text-renderer',
  templateUrl: './rich-text-renderer.component.html',
  styleUrls: ['./rich-text-renderer.component.scss']
})
export class RichTextRendererComponent implements OnInit, OnChanges {
  @Input() text: string = '';
  
  parsedElements: ParsedElement[] = [];
  pdfLoading: any = {};

  constructor(private api: ApiService, private ipcV2: IpcV2Service) {}

  // Extensible type handlers
  private typeHandlers: { [key: string]: (content: string) => ParsedElement | null } = {
    '$image{': (content: string) => this.parseImageTag(content),
    '$link{': (content: string) => this.parseLinkTag(content),
    '$video{': (content: string) => this.parseVideoTag(content),
    '$audio{': (content: string) => this.parseAudioTag(content),
    '$pdf{': (content: string) => this.parsePdfTag(content)
  };

  ngOnInit() {
    this.parseText();
  }

  ngOnChanges() {
    this.parseText();
  }

  private processEscapeSequences(text: string): string {
    return text
      .replace(/\\r\\n/g, '\n')  // Convert \r\n to actual newline
      .replace(/\\n/g, '\n')     // Convert \n to actual newline
      .replace(/\\r/g, '\r')     // Convert \r to actual carriage return
      .replace(/\\t/g, '\t')     // Convert \t to actual tab
      .replace(/\\\\/g, '\\')    // Convert \\ to actual backslash
      .replace(/\\"/g, '"')      // Convert \" to actual quote
      .replace(/\\'/g, "'");     // Convert \' to actual single quote
  }

  private parseText() {
    if (!this.text) {
      this.parsedElements = [];
      return;
    }

    // First, handle escape sequences
    let processedText = this.processEscapeSequences(this.text);

    const elements: ParsedElement[] = [];
    let currentText = processedText;
    let position = 0;

    while (position < currentText.length) {
      // Find the next special tag
      let nextTagPosition = currentText.length;
      let foundTagType = '';

      // Check for any registered tag types
      for (const tagType of Object.keys(this.typeHandlers)) {
        const tagPosition = currentText.indexOf(tagType, position);
        if (tagPosition !== -1 && tagPosition < nextTagPosition) {
          nextTagPosition = tagPosition;
          foundTagType = tagType;
        }
      }

      // Add text before the tag (if any)
      if (nextTagPosition > position) {
        const textContent = currentText.substring(position, nextTagPosition);
        if (textContent.trim()) {
          elements.push({
            type: 'text',
            content: textContent
          });
        }
      }

      // If we found a tag, parse it
      if (foundTagType) {
        const tagStart = nextTagPosition;
        const tagEnd = currentText.indexOf('}', tagStart);
        
        if (tagEnd !== -1) {
          const tagContent = currentText.substring(tagStart, tagEnd + 1);
          const element = this.typeHandlers[foundTagType](tagContent);
          
          if (element) {
            elements.push(element);
          }
          
          position = tagEnd + 1;
        } else {
          // Malformed tag, treat as text
          elements.push({
            type: 'text',
            content: currentText.substring(position)
          });
          break;
        }
      } else {
        // No more tags, add remaining text
        const remainingText = currentText.substring(position);
        if (remainingText.trim()) {
          elements.push({
            type: 'text',
            content: remainingText
          });
        }
        break;
      }
    }

    this.parsedElements = elements;
  }

  private parseImageTag(tagContent: string): ParsedElement | null {
    // Extract: $image{url, "title"}
    const match = tagContent.match(/\$image\{([^,]+),\s*"([^"]+)"\}/);
    if (match) {
      return {
        type: 'image',
        content: match[2], // title
        url: match[1].trim(),
        title: match[2]
      };
    }
    return null;
  }

  private parseLinkTag(tagContent: string): ParsedElement | null {
    // Extract: $link{url, "title"}
    const match = tagContent.match(/\$link\{([^,]+),\s*"([^"]+)"\}/);
    if (match) {
      return {
        type: 'link',
        content: match[2], // title
        url: match[1].trim(),
        title: match[2]
      };
    }
    return null;
  }

  private parseVideoTag(tagContent: string): ParsedElement | null {
    // Extract: $video{url, "title"}
    const match = tagContent.match(/\$video\{([^,]+),\s*"([^"]+)"\}/);
    if (match) {
      return {
        type: 'video',
        content: match[2], // title
        url: match[1].trim(),
        title: match[2]
      };
    }
    return null;
  }

  private parseAudioTag(tagContent: string): ParsedElement | null {
    // Extract: $audio{url, "title"}
    const match = tagContent.match(/\$audio\{([^,]+),\s*"([^"]+)"\}/);
    if (match) {
      return {
        type: 'audio',
        content: match[2], // title
        url: match[1].trim(),
        title: match[2]
      };
    }
    return null;
  }

  private parsePdfTag(tagContent: string): ParsedElement | null {
    // Extract: $pdf{url, "title"}
    const match = tagContent.match(/\$pdf\{([^,]+),\s*"([^"]+)"\}/);
    if (match) {
      return {
        type: 'pdf',
        content: match[2], // title
        url: match[1].trim(),
        title: match[2]
      };
    }
    return null;
  }

  onImageClick(url: string, title: string) {
    console.log('Image clicked:', { url, title });
  }

  onLinkClick(url: string, title: string) {
    if (this.ipcV2.isElectron()) {
      this.ipcV2
        .sendAndReceive(IpcChannel.OPEN_URL, url)
        .subscribe(
          (res) => {
            console.log('Opening URL via IPC');
          },
          (error) => {
            console.log('IPC failed, falling back to window.open');
            window.open(url, '_blank');
          }
        );
    } else {
      window.open(url, '_blank');
    }
  }

  onVideoClick(url: string, title: string) {
    console.log('Video clicked:', { url, title });
    // Could open in modal or fullscreen
  }

  onAudioClick(url: string, title: string) {
    console.log('Audio clicked:', { url, title });
    // Could open in modal or control playback
  }

  onPdfClick(url: string, title: string) {
    console.log('PDF clicked:', { url, title });
    // Use same approach as viewFile method
    this.pdfLoading[title] = true;
    this.api.downloadFileBlobExt(url).subscribe(data => {
      console.log(data);
      var file = new Blob([data], { type: 'application/pdf' });
      var fileURL = URL.createObjectURL(file);
      this.pdfLoading[title] = false;
      const newWindow: any = window.open();
      newWindow.document.write(`<title>Pdf Reader</title><iframe src="${fileURL}" frameborder="0" style="border:0; top:0px; left:0px; bottom:0px; right:0px; width:100%; height:100%;" allowfullscreen></iframe>`);
    });
  }
}
